import { useEffect, useCallback, useState, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useRegistrationFlow } from '../context/RegistrationFlowContext';
import { ROUTES } from '../config/routes';

/**
 * ナビゲーションをブロックするカスタムフック
 * @param {boolean} shouldBlock - ブロックを有効にするかどうか
 * @param {Function} onNavigateAway - 遷移を許可した際に実行するクリーンアップ関数
 * @param {string} redirectTo - リダイレクト先（デフォルト: ログイン画面）
 * @returns {Object} モーダル制御用のオブジェクト
 */
export const useNavigationBlock = (shouldBlock, onNavigateAway = null, redirectTo = ROUTES.LOGIN) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const { resetFlow } = useRegistrationFlow();
  const isInitialMount = useRef(true);

  // React Routerのナビゲーションをブロック
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        if (isInitialMount.current) {
          return false;
        }
        return shouldBlock && currentLocation.pathname !== nextLocation.pathname;
      },
      [shouldBlock]
    )
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // ブラウザバック/フォワード時の処理
    const handlePopState = (e) => {
      
      // 履歴を元に戻す（ユーザーが実際に戻らないようにする）
      window.history.pushState(null, '', window.location.href);
      
      // モーダルを表示
      setIsModalOpen(true);
    };

    // 初回に履歴エントリを追加
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlock]);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    if (blocker.state === 'blocked') {
      setIsModalOpen(true);
      setPendingNavigation(blocker);
    }
  }, [blocker]);

  const handleConfirm = useCallback(() => {
    setIsModalOpen(false);
    
    if (pendingNavigation) {
      pendingNavigation.reset();
      setPendingNavigation(null);
    }
    
    if (onNavigateAway && typeof onNavigateAway === 'function') {
      try {
        onNavigateAway();
      } catch (error) {
        console.error('[NAVIGATION BLOCK] カスタムクリーンアップに失敗:', error);
      }
    }
    
    if (resetFlow) {
      resetFlow();
    }
    
    window.location.replace(redirectTo);
    
  }, [pendingNavigation, resetFlow, onNavigateAway, redirectTo]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    if (pendingNavigation) {
      pendingNavigation.reset();
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);

  return {
    isModalOpen,
    handleConfirm,
    handleCancel
  };
};